import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Alert,
  Dialog,
  DialogContent,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import DashboardHeader from "@components/Header/DashboardHeader";
import GenericBreadCrums from "@components/Breadcrumbs";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  useNavigate,
  useNavigationType,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  fetchRenewalRequestThunk,
  submitRenewalRequestThunk,
} from "./redux/thunk/renewalThunk";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "@store/store"; // Adjust path to your store file
import { uploadToS3Thunk } from "@store/thunk/UploadS3Thunk";
import { productionCheck } from "@components/buttonGroupView/utils";
import { genericUploadThunk } from "@modules/provisional-payment/redux/thunk/uploadThunk";
import {
  PictureAsPdf,
  PictureAsPdfOutlined,
  RemoveRedEyeOutlined,
} from "@mui/icons-material";
import ImageIcon from "@mui/icons-material/Image";
import {
  getActualFileName,
  openFileBySignedUrl,
} from "../../utils/getViewSignedUrlHandler";
import { getFileSignedURLThunk as getFileSignedROW } from "@modules/stepper-row/redux/thunk/documentThunk";
import {
  deleteFileByIdThunk,
  getDeleteSignedURLThunk,
  getFileSignedURLThunk as getFileSignedAccess,
} from "@modules/stepper/redux/thunk/documentThunk";
import { deleteFileOnAwsAPI } from "../../utils/s3Upload";
import {
  RENEWAL_FORM_QUESTIONS_CASE_A,
  RENEWAL_FORM_QUESTIONS_CASE_B,
  RENEWAL_FORM_QUESTIONS_CASE_C,
  RENEWAL_FORM_QUESTIONS_CASE_D,
  RENEWAL_FORM_QUESTIONS_CASE_E,
} from "./constants/staticRenewalAccessData";

const ApplicationRenewal: React.FC = () => {
  const navigate = useNavigate();
  const navigationType = useNavigationType();

  // Renewal questionnaire state
  const [answers, setAnswers] = useState<Record<string, string>>({});

  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [showCapacityError, setShowCapacityError] = useState(false);

  const [briefDescription, setBriefDescription] = useState("");
  const [renewalFromDate, setRenewalFromDate] = useState<Dayjs | null>(null);
  const [renewalToDate, setRenewalToDate] = useState<Dayjs | null>(null);
  const [relatedDocuments, setRelatedDocuments] = useState<any[]>([]);
  const [relatedPhotos, setRelatedPhotos] = useState<any[]>([]);
  const [renewalId, setRenewalId] = useState<string | null>(null);
  const [roadSafety, setRoadSafety] = useState("");
  const [bearCost, setBearCost] = useState("");

  const documentInputRef = useRef<HTMLInputElement>(null);
  const photosInputRef = useRef<HTMLInputElement>(null);

  const applicationDetails = useSelector(
    (state: any) => state.applicationDetails?.applicationDetails,
  );

  const applicationDetailsROW = useSelector(
    (state: any) => state.applicationDetailsROW?.applicationDetailsROW,
  );

  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const access = searchParams.get("access");
  const row = searchParams.get("row");
  const category = searchParams.get("category");
  const applicationId = access || row;

  const ACTIVE_QUESTIONS = access
    ? category?.includes("Fuel")
      ? RENEWAL_FORM_QUESTIONS_CASE_A
      : RENEWAL_FORM_QUESTIONS_CASE_B
    : category?.includes("Industrial")
      ? RENEWAL_FORM_QUESTIONS_CASE_C
      : category?.includes("BharatNet")
        ? RENEWAL_FORM_QUESTIONS_CASE_E
        : RENEWAL_FORM_QUESTIONS_CASE_D;

  const radioQuestions = ACTIVE_QUESTIONS.filter((q) => q.type === "radio");

  const renewalDescription = ACTIVE_QUESTIONS.find(
    (q) => q.key === "renewal_description",
  );

  const handleFileUpload = async (
    file: File,
    category: string,
  ): Promise<{
    fileName: string;
    objectKey: string;
    originalFileName: string;
  } | null> => {
    if (file.size > 21 * 1024 * 1024) {
      toast.error("File size must be less than 20 MB");
      return null;
    }

    try {
      const res = await dispatch(
        genericUploadThunk({
          file,
          moduleType: access ? "access" : "row",
        }),
      );

      if (!res?.payload?.success) {
        const details =
          res?.payload?.details || res?.payload?.response?.data?.details;

        toast.error(
          Array.isArray(details)
            ? details?.[0]
            : typeof details === "string"
              ? details
              : "Something went wrong!",
        );
        return null;
      }

      const { data } = res.payload;

      // Upload to S3 (if needed)
      if (!productionCheck) {
        await dispatch(
          uploadToS3Thunk({
            signedUrl: data.signedUrl,
            file,
            contentType: file.type,
          }),
        ).unwrap();
      }

      // ✅ Return API-ready format
      return {
        fileName: data.fileName,
        objectKey: data.objectKey,
        originalFileName: file.name,
      };
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error(`Failed to upload ${file.name}`);
      return null;
    }
  };

  // Handler for renewal question changes
  const handleAnswerChange = (key: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));

    setShowCapacityError(false);
  };

  // Check if user can proceed to renewal form (only if capacity augmentation is "no")
  // const canProceedToForm = capacityQuestion;

  // Auto-calculate number of years between from and to date
  const numberOfYears = React.useMemo(() => {
    if (!renewalFromDate || !renewalToDate) return null;
    const diff = renewalToDate.diff(renewalFromDate, "year", true);
    if (diff <= 0) return null;
    const rounded = Math.round(diff);
    return rounded === 1 ? "1 Year" : `${rounded} Years`;
  }, [renewalFromDate, renewalToDate]);

  // Submission denied: if road safety is "no" AND bear cost is "no"
  const submissionDenied = roadSafety === "no" && bearCost === "no";

  const isFormValid =
    (answers["renewal_description"] || "").trim().length > 0 &&
    relatedPhotos.length >= 2 &&
    relatedDocuments.length > 0;

  const breadcrumbData = [
    { label: "Application Details", link: "/application-details" },
    { label: "Apply for Renewal", link: "#" },
  ];

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "document" | "photo",
  ) => {
    const files = Array.from(e.target.files || []);

    for (const file of files) {
      const uploaded = await handleFileUpload(file, type);

      if (uploaded) {
        if (type === "document") {
          setRelatedDocuments((prev) => [...prev, uploaded]);
        } else {
          setRelatedPhotos((prev) => [...prev, uploaded]);
        }
      }
    }

    e.target.value = "";
  };

  const transformRenewalPayload = ({
    applicationId,
    renewalId,
    answers,
    briefDescription,
    relatedDocuments,
    relatedPhotos,
  }: any) => {
    const allDocs = [...relatedDocuments, ...relatedPhotos]
      .filter((doc) => doc?.fileName && doc?.objectKey)
      .map((doc) => ({
        fileName: doc.fileName,
        objectKey: doc.objectKey,
        originalFileName: doc.originalFileName,
      }));

    // 🔥 NEW LOGIC (ID BASED)
    const questions_true_renewal: number[] = [];
    const questions_false_renewal: number[] = [];

    ACTIVE_QUESTIONS.filter((q) => q.type === "radio") // only boolean questions
      .forEach((q) => {
        const value = answers[q.key];

        if (value === "yes") {
          questions_true_renewal.push(q.id);
        } else if (value === "no") {
          questions_false_renewal.push(q.id);
        }
      });

    return {
      ...(renewalId && { id: renewalId }),

      applicationId,

      questions_true_renewal,
      questions_false_renewal,

      renewal_description: answers["renewal_description"] || "",

      renewal_documents: allDocs,
    };
  };

  const handleViewFile = (doc_name: any) => {
    openFileBySignedUrl({
      document_name: doc_name,
      dispatch,
      getSignedUrlThunk: access ? getFileSignedAccess : getFileSignedROW,
    });
  };

  const handleSubmit = async () => {
    if (!isFormValid) return;

    const payload = transformRenewalPayload({
      applicationId: applicationId, // use application_id from ROW if available, else fallback to id
      ...(renewalId && { renewalId }),
      answers,
      briefDescription,
      relatedDocuments,
      relatedPhotos,
    });

    try {
      const res = await dispatch(
        submitRenewalRequestThunk(payload) as any,
      ).unwrap();
      setSuccessMessage(res?.message);
      setSuccessModalOpen(true);

      if (access) {
        navigate(`/renewal-payment?access=${applicationId}`);
      } else if (row) {
        navigate(`/renewal-payment?row=${applicationId}`);
      }
    } catch (err) {
      console.error("Error", err);
      toast.error("Failed to submit renewal request");
    }
  };

  const fetchExistingRenewalData = async () => {
    try {
      const appId = applicationId;
      if (!appId) return;

      const res = await dispatch(
        fetchRenewalRequestThunk(appId) as any,
      ).unwrap();

      const data = res?.data;
      if (!data) return;

      // ✅ store id for edit
      setRenewalId(data.id);

      // 🔥 Map IDs → keys
      const answersMap: Record<string, string> = {};

      // Combine both arrays
      const trueIds = data.questions_true_renewal || [];
      const falseIds = data.questions_false_renewal || [];

      // Loop config (single source of truth)
      ACTIVE_QUESTIONS.forEach((q) => {
        if (q.type === "radio") {
          if (trueIds.includes(q.id)) {
            answersMap[q.key] = "yes";
          } else if (falseIds.includes(q.id)) {
            answersMap[q.key] = "no";
          }
        }
      });

      setAnswers(answersMap);

      setAnswers((prev) => ({
        ...prev,
        [renewalDescription?.key!]: data.renewal_description || "",
      }));

      setRenewalFromDate(
        data.renewal_from_date ? dayjs(data.renewal_from_date) : null,
      );
      setRenewalToDate(
        data.renewal_to_date ? dayjs(data.renewal_to_date) : null,
      );

      // ✅ Split documents by type
      const docs = data.renewal_documents || [];

      const documents = docs.filter((doc: any) =>
        doc.fileName.match(/\.(pdf|doc|docx)$/i),
      );

      const photos = docs.filter((doc: any) =>
        doc.fileName.match(/\.(png|jpg|jpeg)$/i),
      );

      setRelatedDocuments(
        documents.map((doc: any) => ({
          fileName: doc.fileName,
          objectKey: doc.objectKey,
          originalFileName: doc.originalFileName,
        })),
      );

      setRelatedPhotos(
        photos.map((doc: any) => ({
          fileName: doc.fileName,
          objectKey: doc.objectKey,
          originalFileName: doc.originalFileName,
        })),
      );
    } catch (err) {
      console.error(err);
    }
  };

  const photoContent = access
    ? {
      title: "Access Related Photographs",
      points: [
        "Photograph showing the site along with the National Highway",
        "Photograph of access (entry/exit) and visibility, including any obstruction",
      ],
    }
    : {
      title: "Supporting Photographs",
      points: [
        "Location of utility within ROW",
        "Alignment/placement and road condition",
        "Impact on safety (drainage, structures, traffic)",
        "Any deviation, violation, or encroachment",
      ],
    };

  useEffect(() => {
    if (applicationId) {
      fetchExistingRenewalData();
    }
  }, [applicationId]);

  useEffect(() => {
    if (!applicationId) return;

    const blockedApplicationId = sessionStorage.getItem(
      "renewalBackBlockedAppId",
    );

    if (
      navigationType === "POP" &&
      blockedApplicationId === String(applicationId)
    ) {
      navigate("/application-dashboard", { replace: true });
    }
  }, [applicationId, navigate, navigationType]);

  const handleSuccessModalClose = () => {
    setSuccessModalOpen(false);

    if (access) {
      navigate(`/renewal-payment?access=${applicationId}`);
    } else if (row) {
      navigate(`/renewal-payment?row=${applicationId}`);
    }
  };

  return (
    <>
      <Box className="login-root" sx={{ minHeight: "100vh", bgcolor: "#f5f6fa" }}>
        <Box className="page-content" sx={{ px: { xs: 2, md: 4 }, pb: 6 }}>
          <GenericBreadCrums breadcrumbs={breadcrumbData} className="mt-6" />

          {/* Renewal Questionnaire Section */}
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: "12px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
              mt: 2,
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                px: { xs: 2, md: 4 },
                py: 2,
                borderBottom: "1px solid #eee",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <IconButton size="small" onClick={() => navigate(-1)}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Typography fontWeight={700} fontSize={16}>
                Apply for Renewal of License
              </Typography>
            </Box>

            <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
              <Typography fontWeight={600} fontSize={15} mb={3}>
                Please answer the following questions to proceed
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {radioQuestions.map((q, index) => {
                  // 👉 Access flow: show only first question until "no"
                  // if (access && index > 0 && !canProceedToForm) return null;

                  return (
                    <Box key={q.id}>
                      <Typography fontWeight={500} mb={1.5} sx={{ fontSize: 14 }}>
                        {q.label}
                      </Typography>

                      <RadioGroup
                        value={answers[q.key] || ""}
                        onChange={(e) =>
                          handleAnswerChange(q.key, e.target.value)
                        }
                        sx={{ flexDirection: "row" }}
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>

                      {/* 🔥 YES ACTION (capacity block) */}
                      {q.yesAction && (
                        <>
                          <Box
                            sx={{
                              bgcolor: "#FFEBEE",
                              border: "1px solid #EF5350",
                              borderRadius: "6px",
                              p: 2,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 2,
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: "#C5221E",
                              }}
                            >
                              {q.yesAction.message}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              mt: 2,
                            }}
                          >
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => {
                                if (q?.yesAction?.redirectTo) {
                                  navigate(q.yesAction.redirectTo);
                                }
                              }}
                              sx={{
                                borderRadius: "24px",
                                background: "#3A1C73",
                              }}
                            >
                              {q.yesAction.ctaLabel}
                            </Button>
                          </Box>
                        </>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          {/* Renewal Form Section - Only show if capacity augmentation is "no" */}
          {
            <Box
              sx={{
                bgcolor: "#fff",
                borderRadius: "12px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                mt: 2,
                overflow: "hidden",
              }}
            >
              {/* Page Header */}
              <Box
                sx={{
                  px: { xs: 2, md: 4 },
                  py: 2,
                  borderBottom: "1px solid #eee",
                }}
              >
                <Typography fontWeight={700} fontSize={16}>
                  Renewal Details
                </Typography>
              </Box>

              <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
                <Typography fontWeight={600} fontSize={15} mb={3}>
                  Enter and Upload the required details for Renewal
                </Typography>

                <Grid container spacing={3}>
                  {/* Brief Description */}
                  <Grid item xs={12} sm={5}>
                    <Typography fontSize={14} fontWeight={500}>
                      Brief Description of Renewal (Max 500 characters)
                      <span style={{ color: "#C5221E" }}> *</span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      label="Enter Brief Description"
                      value={
                        renewalDescription?.key
                          ? answers[renewalDescription.key] || ""
                          : ""
                      }
                      onChange={(e) => {
                        const value = e.target.value;

                        if (value.length > 500 || !renewalDescription?.key)
                          return;

                        handleAnswerChange(renewalDescription.key, value);
                      }}
                      multiline
                      minRows={1}
                      inputProps={{ maxLength: 500 }}
                      sx={{ bgcolor: "#FAFAFA" }}
                    />
                  </Grid>

                  {/* Related Document */}
                  <Grid item xs={12} sm={5}>
                    <Typography fontSize={14} fontWeight={500}>
                      Related Document{" "}
                      <span style={{ color: "#C5221E" }}> *</span>
                    </Typography>
                    {access && (
                      <Typography fontSize={12} color="text.secondary" mt={0.5}>
                        <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                          <li>
                            <strong>
                              Compliance certificate / self-certification -
                              Mandatory if NH Updated 2L to 4L.
                            </strong>
                            <br />
                            (Applicant's declaration that the outlet is compliant
                            with current access permission norms. Where NH has
                            been upgraded (2L to 4L), a certificate from proposing
                            authority that service road has been built)
                          </li>

                          <li style={{ marginTop: "8px" }}>
                            <strong>
                              Proof of NH status change (if applicable) - Optional
                            </strong>
                            <br />
                            Documentary evidence if NH has been upgraded from
                            2-lane to 4-lane since original permission.
                            <br />
                            Triggers mandatory service road construction at
                            renewal — at applicant's own cost.
                          </li>
                        </ul>
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={7}>
                    <input
                      type="file"
                      ref={documentInputRef}
                      style={{ display: "none" }}
                      accept=".pdf,.doc,.docx"
                      multiple
                      onChange={(e) => handleFileChange(e, "document")}
                    />

                    {relatedDocuments.length > 0 ? (
                      <>
                        {relatedDocuments.map((doc, index) => (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              border: "1px solid #e0e0e0",
                              borderRadius: "6px",
                              px: 2,
                              py: 1,
                              bgcolor: "#FAFAFA",
                              mb: 1,
                            }}
                          >
                            {/* LEFT: File Info */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flex: 1,
                                overflow: "hidden",
                              }}
                            >
                              <PictureAsPdf
                                sx={{ fontSize: 18, color: "#E53935" }}
                              />

                              <Typography
                                fontSize={13}
                                sx={{
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {getActualFileName(doc.fileName)}
                              </Typography>
                            </Box>

                            {/* RIGHT: ACTION BUTTONS */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                ml: 2,
                              }}
                            >
                              <IconButton
                                size="small"
                                sx={{ color: "#3a1c73" }}
                                onClick={() => handleViewFile(doc?.fileName)}
                              >
                                <RemoveRedEyeOutlined fontSize="small" />
                              </IconButton>

                              <IconButton
                                size="small"
                                sx={{ color: "#C5221E" }}
                                onClick={async () => {
                                  try {
                                    const fileName =
                                      doc?.uploadedFile?.document_name ||
                                      doc?.objectKey;

                                    if (!fileName) {
                                      console.warn("File name not found");
                                      return;
                                    }

                                    // 1. Get signed delete URL
                                    const deleteRes = await dispatch(
                                      getDeleteSignedURLThunk(fileName),
                                    ).unwrap();

                                    const signedUrl = deleteRes?.data?.signedUrl;

                                    if (!signedUrl) {
                                      throw new Error("Signed URL not received");
                                    }

                                    // 2. Delete from S3
                                    await deleteFileOnAwsAPI(signedUrl);

                                    // 3. Delete from DB
                                    if (doc?.uploadedFile?.id) {
                                      await dispatch(
                                        deleteFileByIdThunk({
                                          applicationId,
                                          document_id: doc.uploadedFile.id,
                                        }),
                                      ).unwrap();
                                    }

                                    // 4. Update UI
                                    setRelatedDocuments((prev) =>
                                      prev.filter(
                                        (item) =>
                                          item.objectKey !== doc.objectKey,
                                      ),
                                    );
                                  } catch (error) {
                                    console.error("Error deleting file:", error);
                                  }
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        ))}

                        {/* Add More Button */}
                        {relatedDocuments.length < 4 && (
                          <Button
                            size="small"
                            onClick={() => documentInputRef.current?.click()}
                            sx={{ mt: 1 }}
                          >
                            + Add More
                          </Button>
                        )}
                      </>
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AttachFileIcon />}
                        onClick={() => documentInputRef.current?.click()}
                        sx={{
                          borderRadius: "6px",
                          borderColor: "#e0e0e0",
                          color: "#555",
                          textTransform: "none",
                          fontSize: 13,
                          bgcolor: "#FAFAFA",
                        }}
                      >
                        Upload File
                      </Button>
                    )}
                  </Grid>

                  {/* Related Photos */}
                  <Grid item xs={12} sm={5}>
                    <Typography fontSize={14} fontWeight={500}>
                      {photoContent.title}{" "}
                      <span style={{ color: "#C5221E" }}> *</span>
                    </Typography>

                    <Typography fontSize={12} color="text.secondary" mt={0.5}>
                      Upload minimum 2 photos covering the following:
                      <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                        {photoContent.points.map((point, index) => (
                          <li key={index}>{point}</li>
                        ))}
                      </ul>
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={7}>
                    <input
                      type="file"
                      ref={photosInputRef}
                      style={{ display: "none" }}
                      accept=".png,.jpg,.jpeg"
                      multiple
                      onChange={(e) => handleFileChange(e, "photo")}
                    />

                    {relatedPhotos.length > 0 ? (
                      <>
                        {relatedPhotos.map((photo, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              border: "1px solid #e0e0e0",
                              borderRadius: "6px",
                              px: 2,
                              py: 1,
                              bgcolor: "#FAFAFA",
                              mb: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                maxWidth: "80%", // 👈 IMPORTANT (or use 100%)
                              }}
                            >
                              <ImageIcon
                                sx={{ fontSize: 18, color: "#16663a" }}
                              />

                              <Typography
                                fontSize={13}
                                sx={{
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  flex: 1, // 👈 allows shrinking inside flex
                                }}
                              >
                                {getActualFileName(photo.fileName)}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "center",
                                gap: 1,
                                minWidth: "80px", // keeps layout stable
                              }}
                            >
                              <IconButton
                                size="small"
                                sx={{ color: "#3a1c73" }}
                                onClick={() => handleViewFile(photo?.fileName)}
                              >
                                <RemoveRedEyeOutlined fontSize="small" />
                              </IconButton>

                              <IconButton
                                size="small"
                                sx={{ color: "#C5221E" }}
                                onClick={async () => {
                                  try {
                                    const fileName =
                                      photo?.uploadedFile?.document_name ||
                                      photo?.objectKey;

                                    if (!fileName) {
                                      console.warn("File name not found");
                                      return;
                                    }

                                    const deleteRes = await dispatch(
                                      getDeleteSignedURLThunk(fileName),
                                    ).unwrap();

                                    const signedUrl = deleteRes?.data?.signedUrl;

                                    if (!signedUrl) {
                                      throw new Error("Signed URL not received");
                                    }

                                    await deleteFileOnAwsAPI(signedUrl);

                                    if (photo?.uploadedFile?.id) {
                                      await dispatch(
                                        deleteFileByIdThunk({
                                          applicationId,
                                          document_id: photo.uploadedFile.id,
                                        }),
                                      ).unwrap();
                                    }

                                    setRelatedPhotos((prev) =>
                                      prev.filter(
                                        (item) =>
                                          item.objectKey !== photo.objectKey,
                                      ),
                                    );
                                  } catch (error) {
                                    console.error("Error deleting photo:", error);
                                  }
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        ))}

                        {/* Add More Button */}
                        {relatedPhotos.length < 4 && (
                          <Button
                            size="small"
                            onClick={() => photosInputRef.current?.click()}
                            sx={{ mt: 1 }}
                          >
                            + Add More
                          </Button>
                        )}
                      </>
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AttachFileIcon />}
                        onClick={() => photosInputRef.current?.click()}
                        sx={{
                          borderRadius: "6px",
                          borderColor: "#e0e0e0",
                          color: "#555",
                          textTransform: "none",
                          fontSize: 13,
                          bgcolor: "#FAFAFA",
                        }}
                      >
                        Upload File
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Box>

              {/* Footer */}
              <Divider />
              <Box
                sx={{
                  px: { xs: 2, md: 4 },
                  py: 2,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  sx={{
                    borderRadius: "24px",
                    borderColor: "#ccc",
                    color: "#555",
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: 14,
                    px: 3,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  disabled={!isFormValid}
                  onClick={handleSubmit}
                  sx={{
                    borderRadius: "24px",
                    background: isFormValid ? "#3A1C73" : "#BDBDBD",
                    color: "#fff",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 14,
                    boxShadow: "none",
                    px: 3,
                    "&:hover": {
                      background: isFormValid ? "#2a145c" : "#BDBDBD",
                    },
                  }}
                >
                  Continue to Pay
                </Button>
              </Box>
            </Box>
          }
        </Box>
      </Box>
      <Dialog
        open={successModalOpen}
        onClose={handleSuccessModalClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogContent sx={{ textAlign: "center" }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <CheckCircleIcon sx={{ fontSize: 56, color: "#2E7D32" }} />
            <Typography fontWeight={700} fontSize={18} mb={1}>
              Success!
            </Typography>
          </Box>
          <Typography fontSize={14} color="text.secondary" mb={3}>
            {successMessage || "Renewal request submitted successfully."}
          </Typography>
          <Button
            variant="contained"
            onClick={handleSuccessModalClose}
            sx={{
              borderRadius: "24px",
              background: "#3A1C73",
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              "&:hover": { background: "#2a145c" },
            }}
          >
            Continue
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ApplicationRenewal;
